import { useEffect, useRef } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import useKeystoreStorageHelpers from 'utils/helpers/keystoreStorage.helpers';
import {
  getCachedPinKey,
  isPinKeyNotFoundError,
  pinKeyEntryToBlob,
  removeCachedPinKey,
  runKeystoreMutation,
  setCachedPinKey,
} from 'utils/helpers/pinKeyCache.helpers';
import { PIN_KEY_PENDING_EVENT } from 'utils/helpers/pinSessionKey.helpers';
import { useFirebaseReadyForUser } from './useFirebaseReadyForUser';
import { usePinLoginEnabled } from './usePinLoginEnabled';
import { usePinOrgId } from './usePinOrgId';

const RETRY_INTERVAL_MS = 10_000;
const MAX_ATTEMPTS = 12;

// A PIN login re-wraps the rotated refresh token before a Firebase session exists for the user, and a
// PIN forgotten while logged out can't be deleted yet either. Both are queued in the local cache and
// written to the keystore here, once the user is signed in.
export const useFlushPendingPinKey = (): void => {
  const { data: user } = useCurrentUser();
  const orgId = usePinOrgId();
  const pinEnabled = usePinLoginEnabled();
  const { uploadPinKey, deletePinKey } = useKeystoreStorageHelpers();
  const helpers = useRef({ uploadPinKey, deletePinKey, pinEnabled });
  helpers.current = { uploadPinKey, deletePinKey, pinEnabled };

  const email = user?.email;
  const userId = user?.userId;
  const firebaseReady = useFirebaseReadyForUser(userId);

  useEffect(() => {
    if (!email || !userId || !orgId || !firebaseReady) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const flush = async () => {
      const entry = getCachedPinKey(orgId, email);
      if (!entry || entry.userId !== userId || !(entry.pendingUpload || entry.pendingDelete)) return;
      // a PIN cached before PIN login was disabled must not recreate the keystore object
      if (!entry.pendingDelete && !helpers.current.pinEnabled) return;

      try {
        if (entry.pendingDelete) {
          try {
            await runKeystoreMutation(entry.pinKeyPath, () => helpers.current.deletePinKey(entry.pinKeyPath));
          } catch (error) {
            if (!isPinKeyNotFoundError(error)) throw error;
          }
          removeCachedPinKey(orgId, email);
        } else {
          let uploaded = false;
          await runKeystoreMutation(entry.pinKeyPath, async () => {
            const latest = getCachedPinKey(orgId, email);
            if (!latest || latest.pendingDelete || !latest.pendingUpload || !helpers.current.pinEnabled) return;
            await helpers.current.uploadPinKey(entry.pinKeyPath, pinKeyEntryToBlob(latest));
            uploaded = true;
          });
          // a newer entry may have been saved during the upload; only settle the one that was uploaded
          const current = getCachedPinKey(orgId, email);
          if (
            uploaded &&
            current &&
            !current.pendingDelete &&
            current.tokenStore.ciphertext === entry.tokenStore.ciphertext
          ) {
            setCachedPinKey(orgId, email, { ...current, pendingUpload: false });
          }
        }
      } catch {
        // the Firebase session may not be ready yet
        if (!cancelled && ++attempts < MAX_ATTEMPTS) timer = setTimeout(flush, RETRY_INTERVAL_MS);
      }
    };

    const onPending = () => {
      attempts = 0;
      flush();
    };

    flush();
    window.addEventListener(PIN_KEY_PENDING_EVENT, onPending);

    return () => {
      window.removeEventListener(PIN_KEY_PENDING_EVENT, onPending);
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [email, userId, orgId, firebaseReady, pinEnabled]);
};
